import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './App.css';

const API_URL = '/api';

function App() {
  // --- STATE ---
  const [view, setView] = useState('login'); 
  const [student, setStudent] = useState(null);
  
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loginStatus, setLoginStatus] = useState('');

  const [activeTab, setActiveTab] = useState('objective');
  const [loading, setLoading] = useState(false);
  const [omrExams, setOmrExams] = useState([]);
  const [subjExams, setSubjExams] = useState([]);
  const [results, setResults] = useState({ omrResults: [], subjectiveResults: [] });

  const [currentExam, setCurrentExam] = useState(null);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState(null);

  const [showStartModal, setShowStartModal] = useState(false);
  const [pendingExam, setPendingExam] = useState(null);

  useEffect(() => {
    const saved = localStorage.getItem('portal_session');
    if (saved) {
      const sess = JSON.parse(saved);
      if (Date.now() < sess.expiry) {
        setStudent(sess.student);
        try {
          const examSess = localStorage.getItem('exam_session');
          if (examSess) {
            const { currentExam: savedExam, answers: savedAnswers } = JSON.parse(examSess);
            if (savedExam && savedExam.endTimeMs > Date.now()) {
              setCurrentExam(savedExam);
              setAnswers(savedAnswers || {});
              setTimeLeft(savedExam.endTimeMs - Date.now());
              setView('exam');
              return;
            } else {
              localStorage.removeItem('exam_session');
            }
          }
        } catch (e) {}
        setView('dashboard');
        loadDashboard(sess.student.studentNo);
      }
    }
  }, []);

  useEffect(() => {
    if (!currentExam) return;
    const s = localStorage.getItem('exam_session');
    if (s) {
      try {
        const sess = JSON.parse(s);
        localStorage.setItem('exam_session', JSON.stringify({ ...sess, answers }));
      } catch (e) {}
    }
  }, [answers, currentExam]);

  useEffect(() => {
    if (!currentExam || !timeLeft) return;
    const interval = setInterval(() => {
      const diff = currentExam.endTimeMs - Date.now();
      if (diff <= 0) {
        clearInterval(interval);
        submitExam(true);
      } else {
        setTimeLeft(diff);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [currentExam, timeLeft]);

  // --- ACTIONS ---

  const handleLogin = async () => {
    if(!mobile || !password) { setLoginStatus('Enter credentials'); return; }
    setLoginStatus('Authenticating...');
    try {
      const res = await axios.post(`${API_URL}/login`, { mobile, password });
      const stu = res.data;
      setStudent(stu);
      localStorage.setItem('portal_session', JSON.stringify({ student: stu, expiry: Date.now() + 43200000 }));
      setView('dashboard');
      loadDashboard(stu.studentNo);
    } catch (e) {
      setLoginStatus(e.response?.data?.message || 'Login failed');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('portal_session');
    setStudent(null);
    setView('login');
    setMobile(''); setPassword('');
  };

  const loadDashboard = async (studentNo) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/dashboard`, { studentNo });
      setOmrExams(res.data.omrExams);
      setSubjExams(res.data.subjectiveExams);
      setResults(res.data.results);
    } catch (e) {
      alert("Failed to load dashboard.");
    } finally {
      setLoading(false);
    }
  };

  const startExam = (exam) => {
    setPendingExam(exam);
    setShowStartModal(true);
  };

  const confirmStartExam = async () => {
    setShowStartModal(false);
    try {
      const res = await axios.post(`${API_URL}/get-omr-exam`, { paperId: pendingExam.paperId, studentNo: student.studentNo });
      const examData = { ...res.data, subject: pendingExam.subject, topic: pendingExam.topic };
      setCurrentExam(examData);
      setAnswers({});
      setTimeLeft(examData.endTimeMs - Date.now());
      localStorage.setItem('exam_session', JSON.stringify({ currentExam: examData, answers: {} }));
      setView('exam');
    } catch (e) {
      alert("Error starting exam: " + (e.response?.data || e.message));
    }
  };

  const continueSavedExam = () => {
    try {
      const s = localStorage.getItem('exam_session');
      if (!s) return;
      const { currentExam: savedExam, answers: savedAnswers } = JSON.parse(s);
      if (savedExam && savedExam.endTimeMs > Date.now()) {
        setCurrentExam(savedExam);
        setAnswers(savedAnswers || {});
        setTimeLeft(savedExam.endTimeMs - Date.now());
        setView('exam');
      } else {
        localStorage.removeItem('exam_session');
      }
    } catch (e) {}
  };

  const submitExam = async (auto = false) => {
    if (!currentExam) return;
    if (!auto && Object.keys(answers).length === 0) {
      alert("Attempt at least one question.");
      return;
    }
    try {
      const responseArr = Object.keys(answers).map(qid => ({
        questionId: qid, answerGiven: answers[qid]
      }));
      await axios.post(`${API_URL}/submit-omr`, {
        student, paperId: currentExam.paperId, responses: responseArr
      });
      alert("Exam submitted!");
      localStorage.removeItem('exam_session');
      setCurrentExam(null);
      setView('dashboard');
      loadDashboard(student.studentNo);
    } catch (e) {
      alert("Submission error.");
    }
  };

  const handlePdfUpload = async (e, exam) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) { alert("File too large (Max 50MB)"); return; }
    if(!window.confirm(`Upload ${file.name}?`)) return;

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = async () => {
      try {
        await axios.post(`${API_URL}/upload-pdf`, {
          student, paperId: exam.paperId, dataUrl: reader.result,
          fileName: `${exam.paperId}_${student.studentNo}.pdf`,
          examDateTime: exam.examDateTimeStr
        });
        alert("Uploaded successfully!");
        loadDashboard(student.studentNo);
      } catch (err) {
        // Show actual server error (likely the quota message if still broken)
        alert("Upload failed: " + (err.response?.data?.message || err.message));
      }
    };
  };

  // --- HELPERS ---
  const formatTime = (ms) => {
    if (ms <= 0) return "00:00";
    const m = Math.floor(ms / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  const getStatus = (exam) => {
    const now = Date.now();
    const start = exam.examDateTimeMs;
    const end = start + (exam.durationMinutes * 60000);
    if (exam.attempted) return 'Attempted';
    if (now < start) return 'Upcoming';
    if (now > end) return 'Missed';
    return 'Live';
  };

  const openResultModal = (resData) => {
    let correct = 0, wrong = 0, unans = 0;
    resData.details.forEach(q => {
      if (!q.answerGiven) unans++;
      else if (q.answerGiven === q.correct) correct++;
      else wrong++;
    });
    setModalData({ ...resData, correct, wrong, unans });
    setShowModal(true);
  };

  // --- RENDER ---

  if (view === 'login') {
    return (
      <div className="page">
        <section className="card login-card">
          <div className="login-header">
            <img src="/MCCLOGO.png" alt="MCC Logo" className="login-logo" />
            <h2>Student Login</h2>
            <p className="login-subtitle">Enter your credentials.</p>
          </div>
          <div className="login-body">
            <div className="login-row">
              <label>Student No.</label>
              <input type="text" value={mobile} onChange={e=>setMobile(e.target.value)} />
            </div>
            <div className="login-row">
              <label>Password</label>
              <div className="password-wrapper">
                <input type={showPass ? "text" : "password"} value={password} onChange={e=>setPassword(e.target.value)} />
                <i className={`fa-regular ${showPass ? "fa-eye-slash" : "fa-eye"} password-toggle`} onClick={()=>setShowPass(!showPass)}></i>
              </div>
            </div>
            <div className="login-actions">
              <span className="login-status">{loginStatus}</span>
              <button className="start-btn" onClick={handleLogin}>Login</button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (view === 'exam') {
    return (
      <div className="page">
        <div className="header exam-header">
          <button className="back-btn" onClick={() => { setView('dashboard'); loadDashboard(student.studentNo); }}>&#8592; Back</button>
          <img src="/MCCLOGO.png" alt="MCC Logo" className="header-logo" />
          <div className="exam-header-info">
            <h1>Taking Exam</h1>
            {currentExam?.subject && <p>{currentExam.subject}{currentExam.topic ? ` — ${currentExam.topic}` : ''}</p>}
          </div>
          <div className="exam-timer">
            <div className="timer-label">Time Left</div>
            <div className="timer-value">{formatTime(timeLeft)}</div>
          </div>
        </div>
        <div className="exam-container">
          {currentExam && currentExam.questions.map((q, i) => (
            <div key={q.questionId} className="omr-question">
              <div className="question-text">Q{i+1}. {q.question}</div>
              {q.imageUrl && <img src={q.imageUrl} alt="q" />}
              <div className="options">
                {['A','B','C','D'].map(opt => (
                  <label key={opt}>
                    <input 
                      type="radio" 
                      name={q.questionId} 
                      checked={answers[q.questionId] === opt}
                      onChange={() => setAnswers({...answers, [q.questionId]: opt})} 
                    />
                    {opt}
                  </label>
                ))}
              </div>
            </div>
          ))}
          <button className="start-btn exam-submit-btn" onClick={() => submitExam(false)}>Submit Test</button>
        </div>
      </div>
    );
  }

  // DASHBOARD VIEW
  return (
    <div className="page">
      <header className="header">
        <img src="/MCCLOGO.png" alt="MCC Logo" className="header-logo" />
        <h1>Student Portal</h1>
      </header>

      <div className="top-bar">
        <div className="welcome-section">
          <h2>Welcome, {student?.name}</h2>
          <p>Batch: {student?.batch}</p>
        </div>
        <div className="top-bar-right">
          <button className="logout-btn" onClick={handleLogout}>Logout</button>
        </div>
      </div>

      <div className="tab-strip">
        <button className={`tab-btn ${activeTab==='objective'?'active':''}`} onClick={()=>setActiveTab('objective')}>Objective</button>
        <button className={`tab-btn ${activeTab==='subjective'?'active':''}`} onClick={()=>setActiveTab('subjective')}>Subjective</button>
        <button className={`tab-btn ${activeTab==='results'?'active':''}`} onClick={()=>setActiveTab('results')}>Results</button>
      </div>

      <div className="tab-content">
        {loading && <div className="no-data">Loading...</div>}

        {/* OBJECTIVE TAB */}
        {!loading && activeTab === 'objective' && (
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Date</th><th>Subject</th><th>Action</th></tr></thead>
              <tbody>
                {[...omrExams].sort((a, b) => b.examDateTimeMs - a.examDateTimeMs).map(ex => {
                  const status = getStatus(ex);
                  return (
                    <tr key={ex.paperId}>
                      <td>
                        <div className="exam-time">{ex.examDateTimeStr}</div>
                        <div className="topic-cell">{ex.durationMinutes} min</div>
                      </td>
                      <td>
                        <div className="subject-cell">{ex.subject}</div>
                        <div className="topic-cell">{ex.topic}</div>
                      </td>
                      <td>
                        {status === 'Live' ? (() => {
                          let isContinuable = false;
                          try {
                            const s = localStorage.getItem('exam_session');
                            if (s) { const ce = JSON.parse(s).currentExam; isContinuable = ce?.paperId === ex.paperId && ce?.endTimeMs > Date.now(); }
                          } catch (e) {}
                          return (
                            <div>
                              <div><span className="live-badge"><span className="live-dot"></span>LIVE</span></div>
                              {isContinuable
                                ? <button className="start-btn continue-btn" onClick={continueSavedExam}>Continue</button>
                                : <button className="start-btn" onClick={() => startExam(ex)}>Start</button>
                              }
                            </div>
                          );
                        })() : (
                          <button className="start-btn grey-btn" disabled>
                            {status === 'Attempted' ? 'Done' : status}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {omrExams.length === 0 && <tr><td colSpan="3" className="no-data">No exams found</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {/* SUBJECTIVE TAB */}
        {!loading && activeTab === 'subjective' && (
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Date</th><th>Subject</th><th>Actions</th></tr></thead>
              <tbody>
                {[...subjExams].sort((a, b) => b.examDateTimeMs - a.examDateTimeMs).map(ex => {
                  const status = getStatus(ex);
                  const isLive = status === 'Live';
                  const isAttempted = ex.attempted;

                  return (
                    <tr key={ex.paperId}>
                      <td>
                        <div className="exam-time">{ex.examDateTimeStr}</div>
                      </td>
                      <td>
                        <div className="subject-cell">{ex.subject}</div>
                        <div className="topic-cell">{ex.topic}</div>
                      </td>
                      <td>
                        <div className="action-buttons">
                          {/* Download Question */}
                          <button 
                            className="download-btn" 
                            disabled={!ex.pdfUrl || status === 'Upcoming'}
                            onClick={() => window.open(ex.pdfUrl, '_blank')}
                          >
                            Paper
                          </button>
                          
                          {/* Upload Answer */}
                          <div className="upload-controls">
                            {isAttempted ? (
                              <button className="start-btn grey-btn" disabled>Attempted</button>
                            ) : (
                              <>
                                {isLive && <div><span className="live-badge"><span className="live-dot"></span>LIVE</span></div>}
                                <input
                                  type="file"
                                  id={`file-${ex.paperId}`}
                                  style={{display:'none'}}
                                  accept="application/pdf"
                                  onChange={(e) => handlePdfUpload(e, ex)}
                                />
                                <button
                                  className={`start-btn ${!isLive ? 'grey-btn' : ''}`}
                                  disabled={!isLive}
                                  onClick={() => document.getElementById(`file-${ex.paperId}`).click()}
                                >
                                  {status === 'Missed' ? 'Missed' : 'Upload'}
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {subjExams.length === 0 && <tr><td colSpan="3" className="no-data">No exams found</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {/* RESULTS TAB */}
        {!loading && activeTab === 'results' && (
          <div className="results-layout">
            
            {/* OMR Results */}
            <div className="table-wrapper">
              <h3 style={{padding:'10px'}}>Objective Results</h3>
              <table>
                <thead><tr><th>Date</th><th>Score</th><th>Details</th></tr></thead>
                <tbody>
                  {results.omrResults.map(r => (
                    <tr key={r.paperId}>
                      <td>
                        <div className="exam-time">{r.examDateTimeStr}</div>
                        <div className="subject-cell">{r.subject}</div>
                      </td>
                      <td><span className="badge">{r.totalMarks}</span></td>
                      <td>
                        {r.isPublished ? (
                          <button className="view-result-btn" onClick={()=>{openResultModal(r)}}>View</button>
                        ) : (
                          <div style={{fontSize:'11px', color:'orange'}}>Pub: {r.publishTimeStr}</div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Subjective Results */}
            <div className="table-wrapper">
              <h3 style={{padding:'10px'}}>Subjective Results</h3>
              <table>
                <thead><tr><th>Date</th><th>Meta</th><th>Result</th></tr></thead>
                <tbody>
                  {results.subjectiveResults.map(r => (
                    <tr key={r.paperId}>
                      <td>
                        <div className="exam-time">{r.examDateTimeStr}</div>
                        <div className="subject-cell">{r.subject}</div>
                      </td>
                      <td>
                        <div style={{display:'flex', flexDirection:'column', gap:'4px'}}>
                          {r.uploadedPdf && <button className="download-btn" onClick={()=>window.open(r.uploadedPdf,'_blank')}>My Copy</button>}
                          {r.checkedPdf && <button className="download-btn" style={{borderColor:'green'}} onClick={()=>window.open(r.checkedPdf,'_blank')}>Checked</button>}
                          {r.isAnswerKeyPublished && r.answerKeyUrl && <button className="download-btn ans-key-btn" onClick={()=>window.open(r.answerKeyUrl,'_blank')}>Ans Key</button>}
                        </div>
                      </td>
                      <td>
                        <div style={{fontWeight:'bold', color:'#4f46e5'}}>{r.examResult || "--"}</div>
                        <div style={{fontSize:'11px'}}>{r.remarks}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

          </div>
        )}
      </div>

      {/* Start Exam Confirmation Modal */}
      {showStartModal && pendingExam && (
        <div className="modal-overlay" onClick={() => setShowStartModal(false)}>
          <div className="modal start-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Start Exam?</h3>
              <button className="modal-close" onClick={() => setShowStartModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="start-modal-info">
                <div className="start-modal-row"><span>Subject</span><b>{pendingExam.subject}</b></div>
                <div className="start-modal-row"><span>Topic</span><b>{pendingExam.topic}</b></div>
                <div className="start-modal-row"><span>Duration</span><b>{pendingExam.durationMinutes} minutes</b></div>
              </div>
              <p className="start-modal-warning">Timer will begin immediately once you start.</p>
              <div className="start-modal-actions">
                <button className="start-btn grey-btn" onClick={() => setShowStartModal(false)}>Cancel</button>
                <button className="start-btn" onClick={confirmStartExam}>Start Now</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* OMR Modal */}
      {showModal && modalData && (
        <div className="modal-overlay" onClick={()=>setShowModal(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h3>Result Details</h3>
              <button className="modal-close" onClick={()=>setShowModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              {/* SUMMARY */}
              <div className="summary-header">
                <span className="pill pill-muted">Date: {modalData.examDateTimeStr}</span>
                <span className="pill pill-muted">Subject: {modalData.subject}</span>
              </div>
              <div className="result-stats-grid">
                <div className="stat-box success">
                  <div className="stat-num">{modalData.correct}</div>
                  <div className="stat-label">Correct</div>
                </div>
                <div className="stat-box danger">
                  <div className="stat-num">{modalData.wrong}</div>
                  <div className="stat-label">Wrong</div>
                </div>
                <div className="stat-box muted">
                  <div className="stat-num">{modalData.unans}</div>
                  <div className="stat-label">Unanswered</div>
                </div>
                <div className="stat-box primary">
                  <div className="stat-num">{modalData.totalMarks}</div>
                  <div className="stat-label">Total Score</div>
                </div>
              </div>

              {/* QUESTIONS */}
              {modalData.details.map((q, i) => (
                <div key={i} className="omr-result-question">
                  <div className="omr-q-title">Q{i+1}. {q.question}</div>
                  {q.imageUrl && <img src={q.imageUrl} alt="Q" style={{maxWidth:'100%', marginBottom:'10px'}} />}
                  <div className="omr-marks">
                    You: <b>{q.answerGiven || '-'}</b> | Key: <b>{q.correct}</b> | Marks: {q.marks}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default App;